import axe, { Result, AxeResults } from "axe-core";
import { isNil } from "lodash";
import { AccessibilityValidationMessage, ValidationMessageImpact, ValidationMessageNode, Validator } from "types/validation";

export const REQUIRED_FIELD_ERROR_TEXT = "is a required field";

export const REQUIRED_LAST_AND_FIRST_OR_CONTACT = "First and Last Name -or- Company name is Required";

const getPhoneNumberErrorText = (numberType: string) =>
    `Enter ${numberType} number in the correct format, like (000) 000-0000 or 000-000-0000`;

export const PHONE_NUMBER_ERROR_TEXT = getPhoneNumberErrorText("phone");

export const CELL_NUMBER_ERROR_TEXT = getPhoneNumberErrorText("cell phone");

export const FAX_NUMBER_ERROR_TEXT = getPhoneNumberErrorText("fax");

export function isNumeric(value: any): boolean {
    return !isNaN(parseFloat(value)) && isFinite(value);
}

/**
 * Check if string is null or contains only whitespace chars
 *
 */
export function isNullOrWhitespace(value: any): boolean {
    return String(value ?? "").trim().length === 0;
}

/**
 * Run accessibility validation on currently open page.
 *
 * @param {boolean} loggingEnabled Print log messages to console if enabled.
 * @returns {Promise<AccessibilityValidationMessage[]>} promise with list of validation messages.
 */
export const validateActivePage = async (loggingEnabled: boolean): Promise<AccessibilityValidationMessage[]> => {
    if (loggingEnabled) {
        console.log("Validating portal:", document.location.toString());
    }

    let results: AccessibilityValidationMessage[] = [];

    try {
        results = await axeValidatorRun(loggingEnabled);
    } catch (error) {
        results.push({
            id: "accessibility.exception",
            message: `Portal validator failed to run`,
            impact: ValidationMessageImpact.Error,
            description: String(error),
            nodes: [],
            url: window.location.toString(),
        });
    }

    if (loggingEnabled) {
        console.log("Validation done. Results:", results);
    }

    return results;
};

/**
 * Run axe validator.
 *
 * @param {boolean} loggingEnabled Print log messages to console if enabled.
 * @returns {Promise<AccessibilityValidationMessage[]>} promise with list of validation messages.
 */
const axeValidatorRun = async (loggingEnabled: boolean): Promise<AccessibilityValidationMessage[]> => {
    const axe = await import("axe-core");
    const results = await axe.run({
        // Exclude Googlemaps container from validation.
        exclude: [[".map-container"]],
    });

    if (loggingEnabled) {
        console.log("axe results:", results);
    }

    return formatAxeResults(results);
};

/**
 * Convert axe validation message to AccessibilityValidationMessage.
 * @param messages list of axe validation messages.
 * @returns list of formatted messages.
 */
const formatAxeResults = (axeResults: AxeResults): AccessibilityValidationMessage[] => {
    const messages = [
        axeResults.violations,
        // Change impact of incomplete results to not show false errors.
        axeResults.incomplete.map<Result>((r) => ({
            ...r,
            impact: ["serious", "critical"].includes(String(r.impact)) ? "moderate" : r.impact,
        })),
    ].flatMap((a) => a);

    const formatMessage = (message: axe.Result): AccessibilityValidationMessage => {
        return {
            id: message.id,
            message: message.help,
            impact: getAxeImpact(message),
            description: message.description,
            nodes: getAxeNodes(message),
            validator: Validator.AxeCore,
            originalMessage: message,
            url: window.location.toString(),
        };
    };

    return (
        messages
            // Filter out warnings about iframe content not tested
            .filter((m) => m.id !== "frame-tested")
            .map((m) => formatMessage(m))
    );
};

const getAxeImpact = (message: axe.Result) => {
    let impact = ValidationMessageImpact.Unknown;
    switch (message.impact) {
        case "critical":
        case "serious":
            impact = ValidationMessageImpact.Error;
            break;
        case "moderate":
            impact = ValidationMessageImpact.Warning;
            break;
        case "minor":
            impact = ValidationMessageImpact.Notice;
            break;
        default:
            break;
    }

    return impact;
};

const getAxeNodes = (message: axe.Result): ValidationMessageNode[] => {
    return message.nodes
        .filter((nodeResult) => !isNil(nodeResult))
        .map((nodeResult) => {
            return {
                selector: nodeResult.target[0],
                innerHtml: nodeResult.html,
            };
        });
};

/**
 * Checks whether a string matches an email pattern.
 */

export const isEmail = (value: string) => {
    const emailEx =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    const emailRegEx = new RegExp(emailEx);

    return emailRegEx.test(value);
};

export const isPhoneNumber = (value: string) => {
    const phoneNumberEx = /^([+]1\s?)?(\d{3}|\(\d{3}\))[\s-]?\d{3}[\s-]?\d{4}$/;
    const phoneNumberRegEx = new RegExp(phoneNumberEx);

    return phoneNumberRegEx.test(value);
};

/**
 * Checks whether a string matches a US or CA postal code pattern.
 */
export const isPostalCode = (value: string) => {
    // Do not validate if value is empty.
    if (isNullOrWhitespace(value)) {
        return true;
    }

    const usZipCodePattern = /^\d{5}(?:[-]\d{4})?$/;
    const canadaPostalCodePattern = /^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/;

    const trimmedValue = value.trim();
    const isUSZipCode = usZipCodePattern.test(trimmedValue);
    const isCanadaPostalCode = canadaPostalCodePattern.test(trimmedValue);

    return isUSZipCode || isCanadaPostalCode;
};
